import {
  Flex,
  Input,
  Textarea,
  Button,
  Card,
  VisuallyHidden,
  Form,
  AlertDialog,
} from '@buffer-mono/popcorn'
import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useAddSavedReplyForm } from '../hooks/savedReplies/useSavedReplyForm'
import styles from './SavedReplyForm.module.css'
import type { SavedReply } from '~publish/gql/graphql'

const MAX_SAVED_REPLY_TITLE_LENGTH = 100
const MAX_SAVED_REPLY_CONTENT_LENGTH = 300

type SavedReplyFormProps = {
  edit: boolean
  open?: boolean
  setOpen?: (open: boolean) => void
  savedReply?: SavedReply
}

export const SavedReplyForm = ({
  edit,
  open = false,
  setOpen,
  savedReply,
}: SavedReplyFormProps): JSX.Element | null => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasDataChanged, setHasDataChanged] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const {
    onSubmit,
    onUpdate,
    content,
    setContent,
    name,
    setName,
    discardDialogVisible,
    showDiscardDialog,
    handleDiscard,
    handleKeepWriting,
    formRef,
  } = useAddSavedReplyForm({
    isEditMode: edit,
    hasDataChanged,
    savedReply,
    setOpen,
    open,
  })

  useEffect(() => {
    /* Focus the textarea when the form is opened */
    if (open && textareaRef.current) {
      const len = textareaRef.current.value.length
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(len, len)
    }
  }, [open])

  const onCancel = (): void => {
    setName('')
    setContent('')
    setOpen?.(false)
  }

  const handleFormSubmit = async (
    event: MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event?.preventDefault()

    try {
      setIsSubmitting(true)
      if (edit) {
        await onUpdate(savedReply?.id ?? '')
      } else {
        onSubmit()
      }
    } finally {
      setIsSubmitting(false)
      onCancel()
    }
  }

  const handleChange = useCallback(
    (value: string, originalValue: string | null | undefined) => {
      setHasDataChanged(value !== originalValue)
      return value
    },
    [],
  )

  const handleChangeName = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setName(handleChange(e.target.value, savedReply?.title)),
    [handleChange, savedReply?.title, setName],
  )

  const handleChangeContent = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) =>
      setContent(handleChange(e.target.value, savedReply?.content.text)),
    [handleChange, savedReply?.content.text, setContent],
  )

  /* If editing, the button is disabled if the data has not changed */
  const disableButton = !content || (edit && !hasDataChanged)

  const savedReplyFormContent = (
    <Form className={styles.form} ref={formRef}>
      <Flex direction="column" gap="sm" align="stretch">
        {/* Name */}
        <Form.Field name="savedReplyName">
          <VisuallyHidden>
            <Form.Label>Name</Form.Label>
          </VisuallyHidden>
          <Form.Control>
            <Input
              className={styles.input}
              maxLength={MAX_SAVED_REPLY_TITLE_LENGTH}
              name="savedReplyName"
              onChange={handleChangeName}
              placeholder="Name"
              size="large"
              type="text"
              value={name ?? ''}
            />
          </Form.Control>
        </Form.Field>

        {/* Content */}
        <Form.Field name="savedReplyContent">
          <VisuallyHidden>
            {' '}
            <Form.Label>Content</Form.Label>
          </VisuallyHidden>
          <Form.Control>
            <Textarea
              ref={textareaRef}
              aria-required
              className={styles.textarea}
              maxLength={MAX_SAVED_REPLY_CONTENT_LENGTH}
              name="savedReplyContent"
              onChange={handleChangeContent}
              placeholder="Write your saved reply"
              required={true}
              rows={3}
              size="large"
              value={content}
            />
          </Form.Control>
        </Form.Field>

        {/* Submit */}
        <Flex direction="row" justify="end" gap="xs">
          <Button
            type="submit"
            disabled={isSubmitting || disableButton}
            className={styles.submitButton}
            onClick={(e) => {
              void handleFormSubmit(e)
            }}
          >
            {isSubmitting ? 'Saving...' : edit ? 'Save changes' : 'Save'}
          </Button>
          <Button
            variant="secondary"
            onClick={onCancel}
            className={styles.cancelButton}
          >
            Cancel
          </Button>
        </Flex>
      </Flex>
      <DiscardChangesDialog
        open={discardDialogVisible}
        onOpenChange={showDiscardDialog}
        onDiscard={handleDiscard}
        onKeepWriting={handleKeepWriting}
      />
    </Form>
  )

  if (!open) {
    return null
  }
  return <Card className={styles.card}>{savedReplyFormContent}</Card>
}

type DiscardChangesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDiscard: () => void
  onKeepWriting: () => void
}

const DiscardChangesDialog = ({
  open,
  onOpenChange,
  onDiscard,
  onKeepWriting,
}: DiscardChangesDialogProps): JSX.Element => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Content>
        <AlertDialog.Title>Discard changes</AlertDialog.Title>
        <AlertDialog.Description>
          You have some unsaved changes. You’ll permanently lose any changes
          you’ve made.
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel onClick={onKeepWriting}>
            <Button variant="tertiary">Keep editing</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action onClick={onDiscard}>
            <Button variant="critical">Discard changes</Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
